// ============================================================================
// DuplicatePointsZones Component (View)
// ============================================================================

import { Box, Typography, Alert, IconButton, Tooltip, Paper, Divider } from "@mui/material";
import { Visibility, VisibilityOff, Delete } from "@mui/icons-material";
import { ConfirmDeleteDialog } from "../../components";
import JobMap from "../../../../../../helpers/JobMap";
import type { DuplicatePointsZonesProps } from "./DuplicatePointsZones.types";
import type { DuplicateGroup } from "../../WizardContainer";
import { useDuplicatePointsZonesViewModel } from "./DuplicatePointsZones.vm";
import { getJobDisplayName } from "../../shared";
import "./DuplicatePointsZones.css";

/**
 * Component for displaying and resolving internal duplicate issues (points/zones within a job)
 */
export function DuplicatePointsZones(props: DuplicatePointsZonesProps) {
	const vm = useDuplicatePointsZonesViewModel(props);

	// No issues state
	if (!vm.hasIssues) {
		return (
			<Box data-testid="no-issues-message" className="duplicate-points-zones no-issues">
				<Alert severity="success">
					<Typography>No duplicate points or zones detected. You can proceed to the next step.</Typography>
				</Alert>
			</Box>
		);
	}

	// No current issue (shouldn't happen but handle gracefully)
	if (!vm.currentIssue) {
		return null;
	}

	const jobName = getJobDisplayName(vm.currentIssue.job);

	return (
		<Box className="duplicate-points-zones">
			{/* Issue Description */}
			<Box className="issue-description">
				<Typography variant="body2" color="text.secondary">
					<strong>{jobName}</strong> contains <strong>{vm.currentIssue.duplicateGroups.length}</strong> set(s) of
					duplicate features.
				</Typography>
			</Box>

			{/* Side-by-side layout: Content left, Map right */}
			<Box className="side-by-side-layout">
				{/* Left side - Duplicate Groups List */}
				<Box className="left-side">
					<Paper variant="outlined" className="groups-panel">
						<Box className="groups-header">
							<Typography variant="subtitle2">Duplicate Groups</Typography>
							<Box className="visibility-controls">
								<Tooltip title="Show All">
									<IconButton size="small" onClick={vm.showAllFeatures}>
										<Visibility fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Hide All">
									<IconButton size="small" onClick={vm.hideAllFeatures}>
										<VisibilityOff fontSize="small" />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
						<Divider />
						<Box className="groups-list">
							{vm.currentIssue.duplicateGroups.map((group, groupIndex) => (
								<DuplicateGroupItem
									key={group.id}
									group={group}
									groupIndex={groupIndex}
									getPositionColor={vm.getPositionColor}
									hiddenFeatureIds={vm.hiddenFeatureIds}
									onToggleVisibility={(featureId) => vm.toggleFeatureVisibility(featureId)}
									onDeleteFeature={(featureIndex) => vm.handleDeleteFeatureClick(group.id, featureIndex, group.type)}
									getFeatureDisplayName={vm.getFeatureDisplayName}
								/>
							))}
						</Box>
					</Paper>
				</Box>

				{/* Right side - Map */}
				<Box className="right-side">
					<JobMap
						jobs={[vm.currentIssue.job]}
						colorMode="boundary-outline"
						duplicateFeatures={vm.mapFeatures}
						hiddenDuplicateFeatureIds={vm.hiddenFeatureIds}
						height="100%"
						hiddenJobIds={[]}
						gestureHandling="cooperative"
					/>
				</Box>
			</Box>

			{/* Delete Confirmation Dialog */}
			<ConfirmDeleteDialog
				open={vm.deleteFeatureInfo !== null}
				title="Delete Feature"
				message="Are you sure you want to delete this feature? This action cannot be undone."
				onConfirm={vm.handleConfirmDelete}
				onCancel={vm.handleCancelDelete}
			/>
		</Box>
	);
}

/**
 * Renders a single duplicate group with its features
 */
interface DuplicateGroupItemProps {
	group: DuplicateGroup;
	groupIndex: number;
	getPositionColor: (positionIndex: number) => string;
	hiddenFeatureIds: Set<string>;
	onToggleVisibility: (featureId: string) => void;
	onDeleteFeature: (featureIndex: number) => void;
	getFeatureDisplayName: (group: DuplicateGroup, index: number) => string;
}

function DuplicateGroupItem({
	group,
	groupIndex,
	getPositionColor,
	hiddenFeatureIds,
	onToggleVisibility,
	onDeleteFeature,
	getFeatureDisplayName,
}: DuplicateGroupItemProps) {
	return (
		<Box className="duplicate-group">
			<Box className="group-header">
				<Typography variant="body2" fontWeight={500}>
					{group.type === "point" ? "Point" : "Zone"} Group #{groupIndex + 1}
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
					({group.features.length} duplicates)
				</Typography>
			</Box>
			<Box className="group-features">
				{group.features.map((feature, i) => {
					const featureIndex = group.featureIndices[i];
					const featureId = `${group.id}-${featureIndex}`;
					const isHidden = hiddenFeatureIds.has(featureId);
					const displayName = getFeatureDisplayName(group, i);
					// Extract filename from full path
					const filePath = feature.properties?.FilePath;
					const fileName = filePath ? filePath.split("/").pop() : null;

					return (
						<Box key={featureId} className="feature-item">
							<Box className="feature-info">
								<Box className="color-swatch-small" style={{ backgroundColor: getPositionColor(i) }} />
								<Typography variant="body2" className={isHidden ? "hidden-feature" : ""}>
									{displayName}
									{fileName && (
										<Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
											({fileName})
										</Typography>
									)}
								</Typography>
							</Box>
							<Box className="feature-actions">
								<Tooltip title={isHidden ? "Show" : "Hide"}>
									<IconButton size="small" onClick={() => onToggleVisibility(featureId)}>
										{isHidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
									</IconButton>
								</Tooltip>
								<Tooltip title="Delete">
									<IconButton size="small" color="error" onClick={() => onDeleteFeature(featureIndex)}>
										<Delete fontSize="small" />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
